import {
  ActivityIdSchema,
  AgentIdSchema,
  AgentSkillIdSchema,
  AssetIdSchema,
  DeliverableIdSchema,
  ElementIdSchema,
  McpServerIdSchema,
  PhaseIdSchema,
  RoleIdSchema,
  StageIdSchema,
  ToolIdSchema,
  WorkPracticeIdSchema,
} from '@dpem/ontology';
import * as z from 'zod';

import {
  AutomationLevelSchema,
  BindingKindSchema,
  DependencyKindSchema,
  ElementKindSchema,
  MethodStateSchema,
  RoleKindSchema,
} from '../common/enums.js';
import { JsonLdDocumentSchema, JsonLdNodeSchema } from '../common/jsonld.js';
import {
  DescriptionSchema,
  IdentifierSchema,
  IsoDateTimeSchema,
  LabelSchema,
  MarkdownSchema,
  OrderSchema,
  QueryIntSchema,
  RevisionSchema,
  SentenceSchema,
  ShortTextSchema,
} from '../common/primitives.js';
import { DiagnosticSchema } from './validation.js';

/**
 * authoring context — element-scoped commands against the whole-graph
 * `MethodVersion` aggregate (architecture §3).
 *
 * Two rules shape every schema here:
 *  1. Every mutating command carries `expectedRevision`. A mismatch is a 409
 *     `REVISION_CONFLICT` carrying the current revision, never a silent write.
 *  2. Rename / move / delete take an `elementId` and no `kind`: ontology ids are
 *     prefixed (`stage:S2`, `act:S2.1`), so the kind is already in the id.
 *     Only creation needs an explicit kind, and there it is the discriminator.
 */

export const ElementLockSchema = z.strictObject({
  elementId: ElementIdSchema,
  holderId: IdentifierSchema,
  holderName: ShortTextSchema,
  acquiredAt: IsoDateTimeSchema,
  expiresAt: IsoDateTimeSchema,
  /** True when the caller holds the lock — drives the "EDIT LOCK · YOU" chip. */
  heldByCaller: z.boolean(),
});
export type ElementLock = z.infer<typeof ElementLockSchema>;

/**
 * Single acknowledgement shape for every graph command. `element` is the
 * ontology node as stored, so the client never needs a second representation of
 * an authored element.
 */
export const GraphCommandResponseSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  revision: RevisionSchema,
  element: JsonLdNodeSchema.nullable(),
  /** Ids the command touched beyond `element` (re-ordered siblings, dropped edges). */
  affectedIds: z.array(ElementIdSchema),
  /** Warning-severity diagnostics; errors would have failed the command. */
  warnings: z.array(DiagnosticSchema),
});
export type GraphCommandResponse = z.infer<typeof GraphCommandResponseSchema>;

export const GetGraphRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
});
export type GetGraphRequest = z.infer<typeof GetGraphRequestSchema>;
export const GetGraphResponseSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  revision: RevisionSchema,
  state: MethodStateSchema,
  graph: JsonLdDocumentSchema,
  locks: z.array(ElementLockSchema),
});
export type GetGraphResponse = z.infer<typeof GetGraphResponseSchema>;

/**
 * One field table per kind, shared by create and update.
 *
 * Both commands need the same list, and keeping two copies is how a field ends up
 * settable at creation and un-editable afterwards. Every entry is optional here;
 * `createBranch` adds what creation additionally requires (`name`, and `order`
 * for the kinds that sit in an ordered spine), and `updateBranch` leaves all of
 * them optional so a PATCH can carry one field.
 *
 * Structural fields are deliberately absent: `name` has its own route, and
 * `parentId`/`order` belong to `/position`, so a drag and a text edit can never
 * arrive as one ambiguous command.
 */
const DESCRIBED = {
  description: DescriptionSchema,
  writeup: MarkdownSchema,
} as const;

const PHASE_FIELDS = { ...DESCRIBED } as const;

const STAGE_FIELDS = {
  ...DESCRIBED,
  isMetaStage: z.boolean(),
  exitCriteria: DescriptionSchema,
  automation: AutomationLevelSchema,
  /**
   * How the agentic variant changes this stage. Prose, not a flag: the compare
   * surface quotes it, and no branch anywhere reads it.
   */
  agenticShift: DescriptionSchema,
  /**
   * `effortShift` and `cycleTimeDelta` are prose, like `agenticShift` beside them, so they
   * take the same cap. The rule the three now share: a field that holds a sentence or more is
   * `DescriptionSchema`; only a name-like value stays `ShortTextSchema`. DPSE's own content
   * settles it — the standard method states its cycle-time delta in several hundred
   * characters, and a 200-character cap would have made the source method unimportable.
   */
  effortShift: DescriptionSchema,
  cycleTimeDelta: DescriptionSchema,
} as const;

const WORK_PRACTICE_FIELDS = {
  ...DESCRIBED,
  workStream: ShortTextSchema,
  mandatory: z.boolean(),
} as const;

const ACTIVITY_FIELDS = {
  ...DESCRIBED,
  /** Why the activity exists, as distinct from the one-line `description`. */
  purpose: DescriptionSchema,
  inputs: z.array(ShortTextSchema).max(64),
  outputs: z.array(ShortTextSchema).max(64),
  performedBy: z.array(RoleIdSchema).max(32),
  mandatory: z.boolean(),
  automation: AutomationLevelSchema,
  agenticShift: DescriptionSchema,
  effortShift: DescriptionSchema,
} as const;

const ROLE_FIELDS = {
  ...DESCRIBED,
  /** Sentences, not names — hence `SentenceSchema`, unlike `inputs`/`outputs` above. */
  responsibilities: z.array(SentenceSchema).max(64),
  /** Set when an agentic role is realised by a catalogued agent. */
  agentId: AgentIdSchema,
  /**
   * The human role this agentic role takes over from. The edge that makes the
   * compare surface possible: without it, an agentic method is a separate list of
   * roles with no stated relationship to the standard one.
   */
  humanRoleId: RoleIdSchema,
} as const;

const GATE_FIELDS = {
  ...DESCRIBED,
  exitCriteria: DescriptionSchema,
  automatedCheck: DescriptionSchema,
  humanApproval: DescriptionSchema,
  /** What this gate's agentic form is equivalent to in review terms. Prose. */
  reviewEquivalent: DescriptionSchema,
  mandatory: z.boolean(),
} as const;

const LENS_FIELDS = {
  ...DESCRIBED,
  /**
   * The ontology term (`dpem:workStreamMapping`) rather than a studio-only
   * `focus` field: a value with no ontology property is dropped on export, so
   * the authoring surface only offers fields the graph can carry.
   */
  workStreamMapping: DescriptionSchema,
  checklist: z.array(SentenceSchema).max(64),
} as const;

const CEREMONY_FIELDS = {
  ...DESCRIBED,
  cadence: ShortTextSchema,
  participants: z.array(RoleIdSchema).max(32),
} as const;

/**
 * kind → its field table, for the contexts that patch an element they do not author.
 *
 * The workshop needs exactly this vocabulary. A facilitator tailoring DPSE for a client
 * renames a stage, rewrites an activity's purpose and clears a gate's automated check —
 * the same edits an author makes, made against someone else's baseline and held as an
 * override until the variant is published. Declaring a second field list over there is
 * how "settable in authoring, silently dropped in a workshop" happens, so there is one
 * list and both read it.
 *
 * `role` carries `roleKind` here, matching `UpdateRoleCommandSchema` below and not
 * `ROLE_FIELDS`: an override may turn a human role agentic for a client that has the
 * agents, and the update route has always allowed it.
 */
export const ELEMENT_FIELD_TABLES = Object.freeze({
  phase: PHASE_FIELDS,
  stage: STAGE_FIELDS,
  workPractice: WORK_PRACTICE_FIELDS,
  activity: ACTIVITY_FIELDS,
  role: { ...ROLE_FIELDS, roleKind: RoleKindSchema },
  gate: GATE_FIELDS,
  lens: LENS_FIELDS,
  ceremony: CEREMONY_FIELDS,
} as const);

/**
 * The two modalities the field tables are read in. `Object.fromEntries` cannot
 * preserve key types, so each ends in a key-preservation cast — it re-states the
 * mapping TypeScript loses, and changes no value.
 */
const optionalFields = <TFields extends z.ZodRawShape>(fields: TFields) =>
  Object.fromEntries(
    Object.entries(fields).map(([key, schema]) => [key, (schema as z.ZodType).optional()]),
  ) as unknown as { [K in keyof TFields]: z.ZodOptional<TFields[K]> };

/**
 * For updates: absent means "leave unchanged", explicit `null` means "clear it".
 * Without that distinction a PATCH could never empty a field, because an omitted
 * key and a cleared one would look identical on the wire.
 */
export const clearableFields = <TFields extends z.ZodRawShape>(fields: TFields) =>
  Object.fromEntries(
    Object.entries(fields).map(([key, schema]) => [key, (schema as z.ZodType).nullable().optional()]),
  ) as unknown as { [K in keyof TFields]: z.ZodOptional<z.ZodNullable<TFields[K]>> };

/** Fields every create branch shares. */
const createBase = {
  methodVersionId: IdentifierSchema,
  expectedRevision: RevisionSchema,
  /** Authors may pin a canonical legacy id (`stage:S2`); otherwise the server mints one. */
  id: ElementIdSchema.optional(),
  name: LabelSchema,
} as const;

/** The kinds that sit in an ordered spine and therefore need a position at birth. */
const ordered = { order: OrderSchema } as const;

const createBranch = <const TKind extends string, TExtra extends z.ZodRawShape>(
  kind: TKind,
  extra: TExtra,
) => z.strictObject({ ...createBase, kind: z.literal(kind), ...extra });

/**
 * `optionalFields`, like every other create branch — and it is worth saying why, because
 * this line was the exception and the exception was a bug.
 *
 * Spread raw, `PHASE_FIELDS` made `description` and `writeup` *required* at creation, so
 * the one gesture that creates a phase — name it on the canvas and press create — could
 * not produce a valid command at all: the client's own `checked()` rejected it with two
 * `expected string, received undefined` issues before a request was ever sent. A phase is
 * created in a second and described over the following week, which is exactly what
 * `optionalFields` says.
 */
export const CreatePhaseCommandSchema = createBranch('phase', {
  ...ordered,
  ...optionalFields(PHASE_FIELDS),
});
export const CreateStageCommandSchema = createBranch('stage', {
  ...ordered,
  phaseId: PhaseIdSchema.optional(),
  ...optionalFields(STAGE_FIELDS),
});
export const CreateWorkPracticeCommandSchema = createBranch('workPractice', {
  ...ordered,
  stageId: StageIdSchema.optional(),
  parentWorkPracticeId: WorkPracticeIdSchema.optional(),
  ...optionalFields(WORK_PRACTICE_FIELDS),
});
export const CreateActivityCommandSchema = createBranch('activity', {
  ...ordered,
  workPracticeId: WorkPracticeIdSchema.optional(),
  stageId: StageIdSchema.optional(),
  ...optionalFields(ACTIVITY_FIELDS),
});
export const CreateRoleCommandSchema = createBranch('role', {
  roleKind: RoleKindSchema,
  ...optionalFields(ROLE_FIELDS),
});
export const CreateGateCommandSchema = createBranch('gate', {
  ...ordered,
  stageId: StageIdSchema.optional(),
  ...optionalFields(GATE_FIELDS),
});
export const CreateLensCommandSchema = createBranch('lens', { ...optionalFields(LENS_FIELDS) });
export const CreateCeremonyCommandSchema = createBranch('ceremony', { ...optionalFields(CEREMONY_FIELDS) });

export const CreateElementRequestSchema = z.discriminatedUnion('kind', [
  CreatePhaseCommandSchema,
  CreateStageCommandSchema,
  CreateWorkPracticeCommandSchema,
  CreateActivityCommandSchema,
  CreateRoleCommandSchema,
  CreateGateCommandSchema,
  CreateLensCommandSchema,
  CreateCeremonyCommandSchema,
]);
export type CreateElementRequest = z.infer<typeof CreateElementRequestSchema>;
export const CreateElementResponseSchema = GraphCommandResponseSchema;
export type CreateElementResponse = z.infer<typeof CreateElementResponseSchema>;

/**
 * Editing an element's content after it exists — the surface an author spends
 * almost all their time in, since a phase is created once and its writeup is
 * revised for weeks.
 *
 * `kind` is the discriminator even though the element id already encodes the kind,
 * because it is what lets zod pick the right field set and reject `cadence` on a
 * gate. The server checks the two agree; a mismatch is a validation failure, not a
 * silent preference for one of them.
 *
 * An absent key means "leave unchanged" and an explicit `null` means "clear it".
 * Optional-and-nullable is therefore deliberate: without the distinction, clearing
 * a field would be impossible through a PATCH that omits absent keys.
 */
const updateBranch = <const TKind extends string, TFields extends z.ZodRawShape>(
  kind: TKind,
  fields: TFields,
) =>
  z.strictObject({
    methodVersionId: IdentifierSchema,
    elementId: ElementIdSchema,
    expectedRevision: RevisionSchema,
    kind: z.literal(kind),
    ...clearableFields(fields),
  });

export const UpdatePhaseCommandSchema = updateBranch('phase', PHASE_FIELDS);
export const UpdateStageCommandSchema = updateBranch('stage', STAGE_FIELDS);
export const UpdateWorkPracticeCommandSchema = updateBranch('workPractice', WORK_PRACTICE_FIELDS);
export const UpdateActivityCommandSchema = updateBranch('activity', ACTIVITY_FIELDS);
export const UpdateRoleCommandSchema = updateBranch('role', {
  ...ROLE_FIELDS,
  roleKind: RoleKindSchema,
});
export const UpdateGateCommandSchema = updateBranch('gate', GATE_FIELDS);
export const UpdateLensCommandSchema = updateBranch('lens', LENS_FIELDS);
export const UpdateCeremonyCommandSchema = updateBranch('ceremony', CEREMONY_FIELDS);

export const UpdateElementRequestSchema = z.discriminatedUnion('kind', [
  UpdatePhaseCommandSchema,
  UpdateStageCommandSchema,
  UpdateWorkPracticeCommandSchema,
  UpdateActivityCommandSchema,
  UpdateRoleCommandSchema,
  UpdateGateCommandSchema,
  UpdateLensCommandSchema,
  UpdateCeremonyCommandSchema,
]);
export type UpdateElementRequest = z.infer<typeof UpdateElementRequestSchema>;
export const UpdateElementResponseSchema = GraphCommandResponseSchema;
export type UpdateElementResponse = z.infer<typeof UpdateElementResponseSchema>;

export const RenameElementRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  elementId: ElementIdSchema,
  expectedRevision: RevisionSchema,
  name: LabelSchema,
});
export type RenameElementRequest = z.infer<typeof RenameElementRequestSchema>;
export const RenameElementResponseSchema = GraphCommandResponseSchema;
export type RenameElementResponse = z.infer<typeof RenameElementResponseSchema>;

/**
 * Change an element's canonical ontology id.
 *
 * Renaming and reidentifying are separate commands because they are separate acts.
 * A rename changes the label a reader sees; this changes the identity a published
 * document, a workshop tailoring decision and a plan task all cite. Every relation
 * *inside* the graph keys on row uuids, so the graph itself survives untouched —
 * what does not survive is a citation held in another context, which is why the
 * command refuses when there are dependants unless the author says `force`.
 *
 * The prefix is not negotiable: `phase:start` may become `phase:launch`, never
 * `stage:launch`. The server checks the new id against the element's own kind.
 */
export const ChangeElementIdRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  elementId: ElementIdSchema,
  expectedRevision: RevisionSchema,
  /** The new canonical id. Same kind prefix, new local part. */
  id: ElementIdSchema,
  /**
   * Proceed even though workshops or plans on this version cite the old id.
   *
   * A plain boolean, not `z.stringbool()`: this travels in a PATCH body as JSON, where
   * `true` is a boolean. The coerced form is for `cascade`, which rides on a query
   * string because DELETE carries no body.
   */
  force: z.boolean().optional(),
});
export type ChangeElementIdRequest = z.infer<typeof ChangeElementIdRequestSchema>;
export const ChangeElementIdResponseSchema = GraphCommandResponseSchema;
export type ChangeElementIdResponse = z.infer<typeof ChangeElementIdResponseSchema>;

export const MoveElementRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  elementId: ElementIdSchema,
  expectedRevision: RevisionSchema,
  /** `null` detaches the element from its parent (it becomes graph-level). */
  parentId: ElementIdSchema.nullable(),
  order: OrderSchema,
});
export type MoveElementRequest = z.infer<typeof MoveElementRequestSchema>;
export const MoveElementResponseSchema = GraphCommandResponseSchema;
export type MoveElementResponse = z.infer<typeof MoveElementResponseSchema>;

export const DeleteElementRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  elementId: ElementIdSchema,
  expectedRevision: QueryIntSchema,
  /** Without cascade, deleting a parent with children is rejected as a conflict. */
  cascade: z.stringbool().optional(),
});
export type DeleteElementRequest = z.infer<typeof DeleteElementRequestSchema>;
export const DeleteElementResponseSchema = GraphCommandResponseSchema;
export type DeleteElementResponse = z.infer<typeof DeleteElementResponseSchema>;

export const AddDependencyEdgeRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  expectedRevision: RevisionSchema,
  dependencyKind: DependencyKindSchema,
  fromId: ElementIdSchema,
  toId: ElementIdSchema,
});
export type AddDependencyEdgeRequest = z.infer<typeof AddDependencyEdgeRequestSchema>;
export const AddDependencyEdgeResponseSchema = GraphCommandResponseSchema;
export type AddDependencyEdgeResponse = z.infer<typeof AddDependencyEdgeResponseSchema>;

/** DELETE carries no body, so the edge identity travels as query parameters. */
export const RemoveDependencyEdgeRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  expectedRevision: QueryIntSchema,
  dependencyKind: DependencyKindSchema,
  fromId: ElementIdSchema,
  toId: ElementIdSchema,
});
export type RemoveDependencyEdgeRequest = z.infer<typeof RemoveDependencyEdgeRequestSchema>;
export const RemoveDependencyEdgeResponseSchema = GraphCommandResponseSchema;
export type RemoveDependencyEdgeResponse = z.infer<typeof RemoveDependencyEdgeResponseSchema>;

const bindBase = {
  methodVersionId: IdentifierSchema,
  activityId: ActivityIdSchema,
  expectedRevision: RevisionSchema,
} as const;

/**
 * Binding carries the target's identity and nothing else.
 *
 * There is deliberately no `mandatory` flag: binding is idempotent by
 * `(activity, kind, target)`, so a flag on this command could be set at the first
 * bind and never changed again, and only `activity_deliverable_bindings` has a
 * column to hold one — the other five kinds would accept the field and drop it.
 * Advisory-vs-required belongs to a command that can edit an existing binding.
 */
const bindBranch = <const TKind extends string, TTarget extends z.ZodType>(
  kind: TKind,
  targetId: TTarget,
) =>
  z.strictObject({
    ...bindBase,
    bindingKind: z.literal(kind),
    targetId,
  });

export const BindDeliverableCommandSchema = bindBranch('deliverable', DeliverableIdSchema);
export const BindToolCommandSchema = bindBranch('tool', ToolIdSchema);
export const BindAssetCommandSchema = bindBranch('asset', AssetIdSchema);
export const BindAgentCommandSchema = bindBranch('agent', AgentIdSchema);
export const BindAgentSkillCommandSchema = bindBranch('agentSkill', AgentSkillIdSchema);
export const BindMcpServerCommandSchema = bindBranch('mcpServer', McpServerIdSchema);

export const BindResourceRequestSchema = z.discriminatedUnion('bindingKind', [
  BindDeliverableCommandSchema,
  BindToolCommandSchema,
  BindAssetCommandSchema,
  BindAgentCommandSchema,
  BindAgentSkillCommandSchema,
  BindMcpServerCommandSchema,
]);
export type BindResourceRequest = z.infer<typeof BindResourceRequestSchema>;
export const BindResourceResponseSchema = GraphCommandResponseSchema;
export type BindResourceResponse = z.infer<typeof BindResourceResponseSchema>;

export const UnbindResourceRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  activityId: ActivityIdSchema,
  bindingKind: BindingKindSchema,
  targetId: ElementIdSchema,
  expectedRevision: QueryIntSchema,
});
export type UnbindResourceRequest = z.infer<typeof UnbindResourceRequestSchema>;
export const UnbindResourceResponseSchema = GraphCommandResponseSchema;
export type UnbindResourceResponse = z.infer<typeof UnbindResourceResponseSchema>;

export const AcquireElementLockRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  elementId: ElementIdSchema,
  /** Soft lock: it expires so a closed browser tab cannot block the method. */
  ttlSeconds: z.int().min(30).max(3_600).optional(),
});
export type AcquireElementLockRequest = z.infer<typeof AcquireElementLockRequestSchema>;
export const AcquireElementLockResponseSchema = z.strictObject({ lock: ElementLockSchema });
export type AcquireElementLockResponse = z.infer<typeof AcquireElementLockResponseSchema>;

export const ReleaseElementLockRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
  elementId: ElementIdSchema,
});
export type ReleaseElementLockRequest = z.infer<typeof ReleaseElementLockRequestSchema>;
export const ReleaseElementLockResponseSchema = z.strictObject({
  elementId: ElementIdSchema,
  releasedAt: IsoDateTimeSchema,
});
export type ReleaseElementLockResponse = z.infer<typeof ReleaseElementLockResponseSchema>;

export const ListElementLocksRequestSchema = z.strictObject({
  methodVersionId: IdentifierSchema,
});
export type ListElementLocksRequest = z.infer<typeof ListElementLocksRequestSchema>;
export const ListElementLocksResponseSchema = z.strictObject({
  locks: z.array(ElementLockSchema),
});
export type ListElementLocksResponse = z.infer<typeof ListElementLocksResponseSchema>;

/** Exported for UI affordances that need the authorable kind list with labels. */
export const ELEMENT_KIND_SCHEMA = ElementKindSchema;
export const ElementKindLabels: Readonly<Record<z.infer<typeof ElementKindSchema>, string>> = {
  phase: 'Phase',
  stage: 'Stage',
  workPractice: 'Work practice',
  activity: 'Activity',
  role: 'Role',
  gate: 'Gate',
  lens: 'Lens',
  ceremony: 'Ceremony',
};
